/**
 * Spec 083 — typed client for page-evaluation reads + writes.
 *
 *   POST /api/v1/brands/:brandId/pages/evaluations            — enqueue
 *   GET  /api/v1/brands/:brandId/pages/evaluations            — list
 *   GET  /api/v1/brands/:brandId/pages/evaluations/by-url     — lookup by URL
 *   GET  /api/v1/evaluations/:id                              — detail
 *
 * All responses are wrapped in `{ data }` envelopes (GEO Observatory convention).
 * Consumed by the Admin → Page Evaluations pages (list / new / detail) and
 * by the inline "Evaluate this URL" CTA on /sources/urls.
 */
import { apiFetch, apiGet } from '../lib/api.js';

export type PageEvalStatus = 'pending' | 'running' | 'completed' | 'failed';

export type RuleOutcome = 'pass' | 'fail' | 'partial' | 'failed';

export type EvaluationMode = 'g_eval' | 'dag';

/**
 * Structured "suggested improvement" payload attached to a failing rule.
 *
 * `tone_guidance_applied` (spec 151 Phase B) is the verbatim excerpt of the
 * brand's tone-of-voice guide passage that shaped the `after` rewrite. It is
 * null/absent when the guide did not influence the suggestion — and absent
 * entirely on rows written before this field existed, so all members are
 * optional and the shape stays tolerant of older data.
 */
export interface SuggestedImprovement {
  before?: string;
  after?: string;
  rationale?: string;
  tone_guidance_applied?: { excerpt: string } | null;
  [key: string]: unknown;
}

export interface PageEvaluationListItem {
  id: string;
  brand_id: string;
  page_url: string;
  status: PageEvalStatus;
  aggregate_score: number | null;
  rules_evaluated: number;
  rules_passed: number;
  rules_failed: number;
  rules_partial: number;
  content_type_detected: string | null;
  partial_completion: boolean;
  started_at: string;
  completed_at: string | null;
}

export interface RuleEvalResult {
  id: string;
  rule_id: string;
  rule_slug: string;
  rule_version: number;
  rule_weight: number;
  outcome: RuleOutcome;
  score: number | null;
  reasoning: string | null;
  supporting_quote: string | null;
  suggested_improvement: SuggestedImprovement | null;
  evaluation_mode: EvaluationMode;
  llm_tokens_used: number | null;
  judge_model_version: string;
  evaluated_at: string;
}

export interface PageContentSnapshot {
  extracted_markdown: string;
  extracted_headings: Record<string, unknown>;
  detected_schema: Record<string, unknown>;
  detected_faqs: Record<string, unknown>;
  js_required: boolean;
  fetch_status_code: number;
  last_modified_header: string | null;
  date_modified_schema: string | null;
  fetched_at: string;
  content_hash: string;
}

export interface PageEvaluationDetail extends PageEvaluationListItem {
  page_url_hash: string;
  page_content_hash: string | null;
  rule_set_version: string;
  judge_model_version: string;
  evaluation_cost_usd: number;
  error: string | null;
  results: RuleEvalResult[];
  snapshot: PageContentSnapshot | null;
  // Spec 159 — the brand's tone-of-voice guide pinned at eval time (null = none),
  // and whether the brand's CURRENT guide has since diverged from that snapshot.
  tone_of_voice_guide_snapshot: string | null;
  tone_guide_differs_from_current: boolean;
  // Spec 162 — false for pre-159 rows where the guide was never recorded (so a
  // NULL snapshot reads as "not recorded" rather than the definite "none set").
  tone_guide_snapshot_captured: boolean;
}

export interface CreateEvaluationResponse {
  evaluation_id: string;
  status: PageEvalStatus;
  cached: boolean;
}

export interface ListEvaluationsFilters {
  status?: PageEvalStatus;
  from?: string;
  to?: string;
  min_score?: number;
  max_score?: number;
  limit?: number;
  offset?: number;
}

export interface ListEvaluationsResponse {
  data: PageEvaluationListItem[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Create an evaluation job. The backend returns 200 when a cached result is
 * reused within the 60-minute dedup window, or 202 when a new job is queued.
 * Callers can branch on `cached` to render the appropriate toast.
 */
export async function createEvaluation(
  brandId: string,
  payload: { page_url: string; force_refresh?: boolean },
): Promise<CreateEvaluationResponse> {
  const res = await apiFetch(
    `/api/v1/brands/${brandId}/pages/evaluations`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  if (!res.ok) {
    const text = await res.text();
    let message = `Evaluation request failed: ${res.status}`;
    try {
      const parsed = JSON.parse(text) as { error?: string };
      if (parsed.error) message = parsed.error;
    } catch {
      message = text.slice(0, 200) || message;
    }
    const err = new Error(message) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  const json = (await res.json()) as { data: CreateEvaluationResponse };
  return json.data;
}

export async function listEvaluations(
  brandId: string,
  filters: ListEvaluationsFilters = {},
): Promise<ListEvaluationsResponse> {
  const q = new URLSearchParams();
  if (filters.status) q.set('status', filters.status);
  if (filters.from) q.set('from', filters.from);
  if (filters.to) q.set('to', filters.to);
  if (typeof filters.min_score === 'number') q.set('min_score', String(filters.min_score));
  if (typeof filters.max_score === 'number') q.set('max_score', String(filters.max_score));
  if (typeof filters.limit === 'number') q.set('limit', String(filters.limit));
  if (typeof filters.offset === 'number') q.set('offset', String(filters.offset));
  const url = `/api/v1/brands/${brandId}/pages/evaluations${q.size ? `?${q}` : ''}`;
  return apiGet<ListEvaluationsResponse>(url);
}

export async function getEvaluation(id: string): Promise<PageEvaluationDetail> {
  const res = await apiGet<{ data: PageEvaluationDetail }>(
    `/api/v1/evaluations/${id}`,
  );
  return res.data;
}

/**
 * Permanently delete an evaluation. Child rows (rule results, snapshots) are
 * cascade-deleted at the DB level. Resolves on success; throws an Error with a
 * `.status` field on failure (404 not found, 400 invalid id), mirroring
 * `createEvaluation`'s error handling.
 */
export async function deleteEvaluation(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/evaluations/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const text = await res.text();
    let message = `Delete request failed: ${res.status}`;
    try {
      const parsed = JSON.parse(text) as { error?: string };
      if (parsed.error) message = parsed.error;
    } catch {
      message = text.slice(0, 200) || message;
    }
    const err = new Error(message) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
}

export interface BulkDeleteEvaluationsResponse {
  deleted_count: number;
  deleted_ids: string[];
  skipped_ids: string[];
}

/**
 * Spec 093 — Permanently delete multiple evaluations in a single request.
 * Cascade rules from migration 036 mean rule_evaluation_results,
 * page_content_snapshots, and page_evaluation_jobs rows are removed too.
 *
 *   ids        — 1 to 500 UUIDs.
 *   skipped_ids — input ids that did not match a row (already deleted, never
 *                 existed). Callers should surface this to the operator in
 *                 the success toast wording.
 *
 * Throws on non-2xx with `.status` set; the body's `error` string is used as
 * the thrown message when present.
 */
export async function bulkDeleteEvaluations(
  ids: string[],
): Promise<BulkDeleteEvaluationsResponse> {
  const res = await apiFetch('/api/v1/evaluations/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) {
    const text = await res.text();
    let message = `Bulk delete failed: ${res.status}`;
    try {
      const parsed = JSON.parse(text) as { error?: string };
      if (parsed.error) message = parsed.error;
    } catch {
      message = text.slice(0, 200) || message;
    }
    const err = new Error(message) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  const json = (await res.json()) as { data: BulkDeleteEvaluationsResponse };
  return json.data;
}

export interface PageEvaluationsReport {
  markdown: string;
  generated_at: string;
  page_count: number;
  avg_score: number | null;
}

/**
 * Spec 130 — fetch a markdown report summarising the brand's page evaluations.
 * The file is downloaded client-side and is intended to be fed into a
 * Claude.ai session to render slides (.pptx). Mirrors `getEvaluation`'s
 * `{ data }`-unwrapping convention.
 */
export async function getPageEvaluationsReport(
  brandId: string,
): Promise<PageEvaluationsReport> {
  const res = await apiGet<{ data: PageEvaluationsReport }>(
    `/api/v1/brands/${brandId}/pages/evaluations/report`,
  );
  return res.data;
}

export async function findEvaluationByUrl(
  brandId: string,
  url: string,
): Promise<PageEvaluationListItem | null> {
  const res = await apiGet<{ data: PageEvaluationListItem | null }>(
    `/api/v1/brands/${brandId}/pages/evaluations/by-url?url=${encodeURIComponent(url)}`,
  );
  return res.data;
}
